import React from 'react';
import { createRoot } from 'react-dom/client';
import { useWidgetProps, useWidgetState, useOpenAIGlobal, useOpenAIActions } from 'react-openai-apps-sdk';
import { PostPreviewLayout, PostPreviewSkeleton, ExportPostPreviewDialog } from '@/components/post-preview';
import { SafeArea } from '@/components/SafeArea';

// Mock test data for development
function generateTestData() {
  return {
    _variant: 'desktop',
    _schedulingEnabled: false, // Set to true in dev to test scheduling UI
    id: 1,
    date: '2025-10-14',
    time: '14:30:00',
    type: 2, // GALLERY type
    subtype: 0,
    text: 'Just launched our new product! 🚀 Check it out and let us know what you think. #innovation #tech #startup',
    page: {
      id: 1,
      type: 2, // Instagram
      name: 'Tech Startup Inc',
      username: 'techstartup',
      logo_src: null,
      followers: null,
    },
    attachments: {
      cards: [
        {
          url: '',
          title: '',
          description: '',
          caption: '',
          call_to_action: null,
          medium: {
            id: 1,
            type: 'image',
            sizes: {
              original: {
                src: 'https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=800&h=800&fit=crop',
                width: 1200,
                height: 630,
              },
              lightbox: {
                src: 'https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=800&h=800&fit=crop',
                width: 1200,
                height: 630,
              },
              thumb: {
                src: 'https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=800&h=800&fit=crop',
                width: 360,
                height: 240,
              },
            },
            meta: {
              is_animated_gif: false,
              is_panorama: false,
              is_spherical: false,
            },
          },
        },
      ],
      multi_share_end_card: false,
      location: { name: undefined },
      collaborators: [],
    },
  };
}

// Platform mapping from page.type to platform string
const PAGE_TYPE_TO_PLATFORM = {
  1: 'facebook',
  2: 'instagram',
  3: 'twitter',
  4: 'linkedin',
  5: 'pinterest',
  8: 'google_my_business',
  9: 'threads',
  10: 'tiktok',
  11: 'youtube',
};

// Helper: Convert Kontentino format to Post format
function convertKontentinoToPost(kontentinoData) {
  const { _variant, ...content } = kontentinoData;

  const platform = PAGE_TYPE_TO_PLATFORM[content.page?.type] || 'instagram';

  // Extract attachments from Kontentino cards format
  const attachments = content.attachments?.cards
    ?.map(card => {
      if (!card.medium) return null;
      const url = card.medium.sizes?.original?.src;
      if (!url) return null;
      const type = card.medium.type === 'video' ? 'video' : 'image';
      return { url, type };
    })
    .filter(Boolean) || [];

  return {
    id: String(content.id || Date.now()),
    date: content.date || new Date().toISOString().split('T')[0],
    time: content.time || '12:00',
    platform,
    topic: `${platform.charAt(0).toUpperCase() + platform.slice(1)} Post`,
    text: content.text || '',
    attachments,
    status: 'draft',
    pageName: content.page?.name,
    username: content.page?.username,
  };
}

// Helper: Convert Post format back to Kontentino format for preview
function convertPostToKontentino(post) {
  const pageType = Object.entries(PAGE_TYPE_TO_PLATFORM).find(([_, plat]) => plat === post.platform)?.[0] || 2;

  // Determine post type
  let postType = 1; // COMMENT (text only)
  const videoAttachments = post.attachments?.filter(a => a.type === 'video') || [];
  const imageAttachments = post.attachments?.filter(a => a.type === 'image') || [];

  if (videoAttachments.length > 0) {
    postType = 3; // VIDEO
  } else if (imageAttachments.length > 0) {
    postType = 2; // GALLERY
  }

  // Build image cards
  const imageCards = imageAttachments.map((attachment, index) => ({
    url: '',
    title: '',
    description: '',
    caption: '',
    call_to_action: null,
    medium: {
      id: index + 1,
      type: 'image',
      sizes: {
        original: { src: attachment.url, width: 1200, height: 630 },
        lightbox: { src: attachment.url, width: 1200, height: 630 },
        thumb: { src: attachment.url, width: 360, height: 240 },
      },
      meta: {
        is_animated_gif: false,
        is_panorama: false,
        is_spherical: false,
      },
    },
  }));

  // Build video cards
  const videoCards = videoAttachments.map((attachment, index) => ({
    url: '',
    title: '',
    description: '',
    caption: '',
    call_to_action: null,
    medium: {
      id: 900 + index,
      type: 'video',
      sizes: {
        medium: { src: attachment.url, width: 1280, height: 720 },
      },
      original_thumb: null,
      custom_thumb: null,
      meta: {
        duration: 0,
        mime_type: 'video/mp4',
        file_size: 0,
      },
      status: 3,
    },
  }));

  return {
    id: Number(post.id),
    date: post.date,
    time: post.time,
    type: postType,
    subtype: 0,
    text: post.text,
    page: {
      id: 1,
      type: Number(pageType),
      name: post.pageName || `${post.platform} Page`,
      username: post.username || '',
      logo_src: null,
      followers: null,
    },
    attachments: {
      cards: [...imageCards, ...videoCards],
      multi_share_end_card: false,
    },
  };
}

function PostPreviewWidget() {
  // In dev mode, use mock data generator. In production, use tool output
  const rawData = useWidgetProps(() =>
    import.meta.env.DEV ? generateTestData() : null
  );
  const displayMode = useOpenAIGlobal('displayMode');
  const { sendFollowUpMessage } = useOpenAIActions();

  // Export dialog state - single object instead of multiple states
  const [exportData, setExportData] = React.useState(null);

  // Extract scheduling config from rawData
  const schedulingEnabled = rawData?._schedulingEnabled ?? false;

  // Convert Kontentino data to Post format ONLY when we have rawData
  const convertedPost = React.useMemo(() => {
    if (!rawData) return null;
    const post = convertKontentinoToPost(rawData);
    console.log('[PostPreviewWidget] Converted post:', post);
    return post;
  }, [rawData]);

  // Initialize widgetState with converted post
  const [widgetState, setWidgetState] = useWidgetState(convertedPost);

  // Use widgetState if available, otherwise fall back to convertedPost
  const currentPost = widgetState || convertedPost;

  console.log('[PostPreviewWidget] Render state:', {
    isDev: import.meta.env.DEV,
    hasRawData: !!rawData,
    hasConvertedPost: !!convertedPost,
    hasWidgetState: !!widgetState,
    hasCurrent: !!currentPost,
  });

  // Show skeleton while waiting for data
  if (!currentPost) {
    return <PostPreviewSkeleton />;
  }

  const handlePostUpdate = (updates) => {
    const updatedPost = {
      ...(widgetState || currentPost),
      ...updates,
    };
    setWidgetState(updatedPost);
  };

  const handleSchedule = async (post) => {
    console.log('[Schedule] Sending post to ChatGPT for scheduling:', post);

    const attachmentsSummary = post.attachments?.length > 0
      ? `with ${post.attachments.filter(a => a.type === 'image').length} image(s) and ${post.attachments.filter(a => a.type === 'video').length} video(s)`
      : 'with no media';

    const prompt = `Schedule this ${post.platform} post ${attachmentsSummary} for ${post.date} at ${post.time}:

Text: ${post.text || 'No caption'}

${post.attachments?.length > 0 ? `Attachments:\n${post.attachments.map((a, i) => `${i + 1}. ${a.type}: ${a.url}`).join('\n')}` : ''}

Page: ${post.pageName || 'Unknown'}`;

    const success = await sendFollowUpMessage({
      prompt,
      onSuccess: () => {
        alert('✓ Sent to ChatGPT for scheduling');
      },
      onError: (error) => {
        console.error('[Schedule] Error:', error);
        alert(`⚠ Failed: ${error.message}`);
      },
    });
  };

  const handleExportComplete = (url, filename) => {
    setExportData({ url, filename });
  };

  const handleSendViaEmail = async () => {
    if (!exportData?.url) return;
    await sendFollowUpMessage({
      prompt: `Send this post preview image via email: ${exportData.url}`,
    });
  };

  // Convert Post back to Kontentino format for preview
  const transformedContent = convertPostToKontentino(currentPost);

  return (
    <>
      <SafeArea className="w-full max-w-4xl mx-auto bg-background" fallbackHeight={600}>
        <PostPreviewLayout
          post={currentPost}
          onPostUpdate={handlePostUpdate}
          transformedContent={transformedContent}
          displayMode={displayMode || 'inline'}
          showNavigation={false}
          onSchedule={schedulingEnabled ? handleSchedule : undefined}
          onExportComplete={handleExportComplete}
        />
      </SafeArea>

      <ExportPostPreviewDialog
        open={!!exportData}
        onClose={() => setExportData(null)}
        uploadedUrl={exportData?.url || null}
        filename={exportData?.filename || null}
        onSendViaEmail={handleSendViaEmail}
      />
    </>
  );
}

// Mount to DOM
const rootElement = document.getElementById('post-preview-root');
if (rootElement) {
  createRoot(rootElement).render(<PostPreviewWidget />);
}
